import omit from 'lodash/omit'
import {
    useCallback, useMemo, useState,
} from 'react'
import {
    useLocation,
} from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import getFields from 'app/Apps/Pricing/LaneGroups/LaneFees/LaneFeesTable/LaneFeesForm.fields'
import useGetLaneFee from 'app/Apps/Pricing/LaneGroups/LaneFees/hooks/useGetLaneFee'
import ContractBasisInformationForm from 'app/Apps/Pricing/LaneFees/ContractBasisInformationForm'
import StatusHandler from 'app/shared-components/StatusHandler'
import LaneGroupInformationForm from 'app/Apps/Pricing/LaneFees/LaneGroupInformationForm'
import LaneGroupContextWrapper from 'app/Apps/Pricing/LaneGroups/LaneGroupContextWrapper'

import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import {
    LaneFeesForm, LaneFeesFormContext,
} from 'app/shared-components/LaneFees'
import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import SkyNetStepperButtons from 'app/shared-components/SkyNetStepper/SkyNetStepperButtons'
import LaneGroupRequests from 'app/requests/ContractBasis/LaneGroup/LaneGroup.requests'
import prepareDataForCreation, {
    laneFeesFieldsToOmitOnCopy,
} from 'app/shared-components/LaneFees/LaneFeesCopy/LaneFeesCopy.utils'
import LaneFeesSetStatusButtons from 'app/shared-components/LaneFees/LaneFeesSetStatusButtons'
import WarningCard from 'app/shared-components/WarningCard'

const fields = {
    getFields,
}

export default function LaneFeesFormCopy() {
    const {
        state,
    } = useLocation()

    const {
        LaneFees: LaneFeesRoutes,
    } = usePricingRoutes()

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetLaneFee(state?.originId)

    const [
        edited,
        setEdited,
    ] = useState<boolean>(false)

    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(2)

    const copyData = useMemo(() => {
        return {
            ...omit(data, laneFeesFieldsToOmitOnCopy),
            originId: data?.id,
        }
    }, [data])

    const [
        laneGroupId,
        setLaneGroupId,
    ] = useState<number>(state?.laneGroupId)

    const onSuccess = useCallback(({
        id,
    }: { id: number }) => {
        LaneFeesRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [LaneFeesRoutes.Overview])

    const {
        mutate: create,
        isPending,
    } = useSimpleApiRequest({
        onSuccess,
        name: 'LaneFeesCopy',
        requestParams: LaneGroupRequests.createLaneFee({
            laneGroupId,
        }),
        title: 'Lane Fees Copy Created',
    })

    const getActionButtonsPanel = useCallback(({
        isValid, value,
    }) => {
        return (
            <SkyNetStepperButtons
                isLoading={isPending}
                isValid={isValid}
                customButtons={[<LaneFeesSetStatusButtons
                    key="set-status-buttons"
                    value={value}
                    onCreate={(v) => {
                        setEdited(false)
                        create(prepareDataForCreation(v))
                    }}
                    disabled={!isValid}
                    isLoading={isPending}
                />]}
                disabled={!isValid}
                nextLabel="Create"
                canGoBack={activeStep > 1}
                backAction={() => {
                    setActiveStep(activeStep - 1)
                }}
            />
        )
    }, [
        activeStep,
        create,
        isPending,
    ])

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Select Contract Basis',
                expanded: true,
                content: (
                    <ContractBasisInformationForm
                        id={state?.contractBasisId}
                        disabled
                    />
                ),
            },
            {
                key: '2',
                label: 'Select Lane Group',
                expanded: activeStep > 0,
                content: (
                    <>
                        <LaneGroupInformationForm
                            id={laneGroupId}
                            contractId={state?.contractBasisId}
                            disabled={activeStep !== 1}
                            update={(newValue) => {
                                setEdited(true)
                                setLaneGroupId(newValue.laneGroup.id)
                            }}
                        />
                        <SkyNetStepperButtons
                            isValid={Boolean(laneGroupId)}
                            canEdit={activeStep > 1}
                            nextAction={() => {
                                setActiveStep(activeStep + 1)
                            }}
                            editAction={() => {
                                setActiveStep(1)
                            }}
                        />
                    </>

                ),
            },
            {
                key: '3',
                label: 'Specify Fees',
                expanded: activeStep > 1,
                content: (
                    <LaneGroupContextWrapper id={laneGroupId}>
                        <LaneFeesFormContext.Provider value={fields}>
                            <StatusHandler
                                isSuccess={isDataReady}
                                isFetching={isFetching}
                                isError={isError}
                                error={error}
                            >
                                <>
                                    <WarningCard>
                                        You are copying a Lane Fee.
                                    </WarningCard>
                                    <LaneFeesForm
                                        onChange={(val) => {
                                            setEdited(!isEqual(copyData, val))
                                        }}
                                        data={copyData}
                                        getActionButtonsPanel={getActionButtonsPanel}
                                    />
                                </>
                            </StatusHandler>
                        </LaneFeesFormContext.Provider>
                    </LaneGroupContextWrapper>
                ),
            },
        ]
    }, [
        activeStep,
        copyData,
        error,
        getActionButtonsPanel,
        isDataReady,
        isError,
        isFetching,
        laneGroupId,
        state?.contractBasisId,
    ])

    return (
        <>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={edited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </>
    )
}
